import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { customerProgression, engineCapabilities, expansionNote, expansionStatement, platformProducts, talabScopeNote } from '../../data/deckSlide13';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
type Stage = 0 | 1 | 2 | 3 | 4;
const COLUMN_X = [112, 604, 1096];
const COLUMN_W = 392;
const RAIL_Y = 374;
const COLUMN_TOP = 412;
export function PlatformExpansionSlide() {
  const [stage, setStage] = useState<Stage>(0);
  useEffect(() => {
    const timers: number[] = [];
    const at = (ms: number, next: Stage) => timers.push(window.setTimeout(() => setStage(next), ms));
    at(700, 1); // engine
    at(1700, 2); // the three products
    at(3800, 3); // Talab marketplace expansion
    at(5200, 4); // progression + statement
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const shown = (s: Stage) => stage >= s;
  return <SlideStage data-id="element-2466" data-mp="2llc0tkt3wb2o">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2467" data-mp="2lksvd3kn7f5j">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 65% at 50% -22%, rgba(0,200,83,0.07) 0%, rgba(250,252,249,0) 60%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2468" data-mp="2gm7vj59ayxhm" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2469" data-mp="34dd6p74at1ll">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2470" data-mp="2vdzwgc8co8zt" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2471" data-mp="1nzeq49kyxa1a">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2472" data-mp="1ldnpa5mkhojp">
          14 · Platform Expansion
        </motion.span>

        {/* Headline */}
        <motion.h1 className="absolute text-[40px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 128,
        width: 900
      }} initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        delay: 0.15,
        ease
      }} data-id="element-2473" data-mp="1ggnmc3l0l1wt">
          One AI platform. Multiple products.
        </motion.h1>
        <motion.p className="absolute text-[16px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 190,
        width: 820
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.6,
        ease
      }} data-id="element-2474" data-mp="xlytgfajehy0">
          We built the communication and conversation platform once — then
          commercialize it across individuals, businesses and commerce.
        </motion.p>

        {/* The engine */}
        <motion.div className="absolute text-center" style={{
        left: 500,
        top: 268,
        width: 600
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(1) ? 1 : 0,
        y: shown(1) ? 0 : 10
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2475" data-mp="e3ukdxpb8v10">
          <div className="inline-flex items-center gap-2.5" data-id="element-2476" data-mp="j0kzahpdpkku">
            <img src={VEXU_LOGO} alt="" className="h-5 w-5 object-contain" data-id="element-2477" data-mp="wsj0m6cumc6g" />
            <span className="text-[15px] font-semibold uppercase tracking-[0.2em] text-[#0B1712]" data-id="element-2478" data-mp="wulqmqe045b6">
              VEXU AI Technology
            </span>
          </div>
          <p className="mt-2.5 text-[12px] tracking-[0.04em] text-[#8A998F]" data-id="element-2479" data-mp="16rqc0o00vwqx">
            {engineCapabilities}
          </p>
        </motion.div>

        {/* Branching rail */}
        <motion.div className="absolute w-px" style={{
        left: 800,
        top: 338,
        height: 36,
        backgroundColor: 'rgba(0,200,83,0.4)'
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(2) ? 1 : 0
      }} transition={{
        duration: 0.6,
        ease
      }} data-id="element-2480" data-mp="1lurtnl8ek5la" />
        <motion.div className="absolute h-px origin-center" style={{
        left: COLUMN_X[0] + COLUMN_W / 2,
        top: RAIL_Y,
        width: COLUMN_X[2] + COLUMN_W / 2 - (COLUMN_X[0] + COLUMN_W / 2),
        backgroundColor: 'rgba(0,200,83,0.4)'
      }} initial={{
        scaleX: 0
      }} animate={{
        scaleX: shown(2) ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2481" data-mp="1znhkq42sg0p9" />

        {/* Products */}
        {platformProducts.map((product, i) => {
        const isNext = product.status === 'next';
        return <div key={product.name} data-id="element-2482" data-mp="2s87p7kofmam3" data-mpi={i}>
              <motion.div className="absolute w-px" style={{
            left: COLUMN_X[i] + COLUMN_W / 2,
            top: RAIL_Y,
            height: COLUMN_TOP - RAIL_Y - 12,
            backgroundColor: 'rgba(0,200,83,0.4)'
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: shown(2) ? 1 : 0
          }} transition={{
            duration: 0.6,
            delay: 0.3 + i * 0.12,
            ease
          }} data-id="element-2483" data-mp="gmffso86pfss" />

              <motion.div className="absolute" style={{
            left: COLUMN_X[i],
            top: COLUMN_TOP,
            width: COLUMN_W
          }} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: shown(2) ? 1 : 0,
            y: shown(2) ? 0 : 10
          }} transition={{
            duration: 0.8,
            delay: 0.35 + i * 0.18,
            ease
          }} data-id="element-2484" data-mp="3p2s6typu6ddu">
                <div className="flex items-baseline gap-3" data-id="element-2485" data-mp="1de0w03zkmz3q">
                  <span className="text-[11px] font-semibold tracking-[0.24em] text-vexu-700/60 tabular-nums" data-id="element-2486" data-mp="37tg6eatrrat3">
                    {product.index}
                  </span>
                  <span className="text-[20px] font-semibold tracking-[-0.015em] text-[#0B1712]" data-id="element-2487" data-mp="3clzif3ct5xvs">
                    {product.name}
                  </span>
                  <span className={`rounded-full px-2 py-[3px] text-[9.5px] font-semibold uppercase tracking-[0.18em] ${isNext ? 'bg-vexu/[0.09] text-vexu-700' : 'bg-[#0B1712]/[0.05] text-[#5A6B60]'}`} data-id="element-2488" data-mp="32v4xc2bat4bx">
                    {isNext ? 'Demo ready' : 'Live'}
                  </span>
                </div>
                <p className="mt-2 text-[12.5px] text-[#7C8B82]" data-id="element-2489" data-mp="3hl2c5rnc8hog">
                  {product.audience}
                </p>
                {isNext && <p className="mt-2 text-[11.5px] leading-snug text-vexu-700/80" data-id="element-2490" data-mp="263kqq89mk4qq">
                    {talabScopeNote}
                  </p>}

                <div className="mt-5 space-y-2.5" data-id="element-2491" data-mp="22lwsnvy08c9r">
                  {product.items.map(item => <div key={item} className="flex items-center gap-2.5" data-id="element-2492" data-mp="1xmtwz23wzieg">
                      <span className="rounded-full" style={{
                  width: 4,
                  height: 4,
                  backgroundColor: 'rgba(0,153,64,0.4)'
                }} data-id="element-2493" data-mp="1xowmll15n9in" />
                      <span className="text-[13.5px] leading-tight text-[#33463B]" data-id="element-2494" data-mp="1jtb0pmq7x5ug">
                        {item}
                      </span>
                    </div>)}
                </div>

                {product.highlight && <motion.div className="mt-6" initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: shown(3) ? 1 : 0,
              y: shown(3) ? 0 : 6
            }} transition={{
              duration: 0.8,
              ease
            }} data-id="element-2495" data-mp="1ou8frqfxljrq">
                    <span className="inline-flex items-center rounded-full border border-vexu/25 bg-vexu/[0.07] px-3 py-1.5 text-[12px] font-semibold text-vexu-700" data-id="element-2496" data-mp="aocl3ttwakfr">
                      {product.highlight.label}
                    </span>
                    <p className="mt-3 text-[11.5px] leading-snug text-[#A3AFA7]" data-id="element-2497" data-mp="omsnj8kq5os3">
                      {product.highlight.note}
                    </p>
                  </motion.div>}
              </motion.div>
            </div>;
      })}

        {/* Bottom — the progression across the stack */}
        <motion.div className="absolute border-t border-[#0B1712]/[0.09] pt-6" style={{
        left: 112,
        right: 112,
        bottom: 44
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(4) ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2498" data-mp="13c8dimcflq6q">
          <div className="flex items-center gap-3" data-id="element-2499" data-mp="1ha6pxm76zcjl">
            {customerProgression.map((step, i) => <React.Fragment key={step} data-id="element-2500" data-mp="31u21oyvfybgi" data-mpi={i}>
                {i > 0 && <span className="text-[11px] text-[#C3CCC5]" data-id="element-2501" data-mp="31qmmlw1rp2f3">→</span>}
                <span className="text-[12.5px] uppercase tracking-[0.16em] text-[#7C8B82]" data-id="element-2502" data-mp="2wscr1kc4rzsy">
                  {step}
                </span>
              </React.Fragment>)}
          </div>
          <p className="mt-4 text-[21px] font-semibold tracking-[-0.015em] text-[#0B1712]" data-id="element-2503" data-mp="2nusmo2iz1ne4">
            {expansionStatement}
          </p>
          <p className="mt-2 text-[13px] text-[#7C8B82]" data-id="element-2504" data-mp="3ghvfce0yv3wg">{expansionNote}</p>
        </motion.div>
      </div>
    </SlideStage>;
}